import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { CrescentStarIcon, StarOrnament } from '../common/IslamicIcons';

const RECITED_KEY = 'recited_verse_ids';

// Plant types with SVG-based CSS emoji + colors
const PLANT_TYPES = [
  { emoji: '🌱', color: '#6ee7b7', glow: 'rgba(110,231,183,0.4)' },
  { emoji: '🌸', color: '#f9a8d4', glow: 'rgba(249,168,212,0.4)' },
  { emoji: '🌿', color: '#10b981', glow: 'rgba(16,185,129,0.4)' },
  { emoji: '🌺', color: '#f43f5e', glow: 'rgba(244,63,94,0.4)'  },
  { emoji: '🌻', color: '#fbbf24', glow: 'rgba(251,191,36,0.5)'  },
  { emoji: '🌳', color: '#059669', glow: 'rgba(5,150,105,0.4)'   },
  { emoji: '🌴', color: '#047857', glow: 'rgba(4,120,87,0.4)'    },
  { emoji: '🪷', color: '#c084fc', glow: 'rgba(192,132,252,0.4)' },
];

const MILESTONES = [
  { count: 1,   name: 'First Seed',       nameAr: 'البذرة الأولى',    icon: '🌱', color: '#6ee7b7' },
  { count: 5,   name: 'Seedling Garden',  nameAr: 'حديقة الشتلات',   icon: '🌿', color: '#10b981' },
  { count: 10,  name: 'Blooming Garden',  nameAr: 'الحديقة المزهرة',  icon: '🌸', color: '#f9a8d4' },
  { count: 20,  name: 'Fruitful Garden',  nameAr: 'الحديقة المثمرة',  icon: '🌻', color: '#fbbf24' },
  { count: 50,  name: 'Paradise Garden',  nameAr: 'حديقة النعيم',    icon: '🌴', color: '#047857' },
  { count: 105, name: 'Garden of Jannah', nameAr: 'حديقة الجنة',      icon: '✨', color: '#fbbf24' },
];

const getPlantAt = (index) => PLANT_TYPES[index % PLANT_TYPES.length];
const getMilestone = (n) => [...MILESTONES].reverse().find(m => n >= m.count) || null;
const getNextMilestone = (n) => MILESTONES.find(m => n < m.count);

// Sparkle particles for new plant effect
const SparkleParticles = ({ x, y }) => (
  <div className="absolute pointer-events-none" style={{ left: x, top: y, transform: 'translate(-50%,-50%)' }}>
    {[...Array(6)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1.5 h-1.5 rounded-full bg-amber-400 sparkle-particle"
        style={{
          '--angle': `${i * 60}deg`,
          '--dist': '20px',
          animation: `sparkle-particle-move 0.6s ease-out ${i * 0.04}s forwards`,
        }}
      />
    ))}
    <div className="absolute inset-0 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full border-2 border-amber-400 sparkle-ring" />
  </div>
);

// Individual plant cell
const PlantCell = React.memo(({ plant, isNew, index, total }) => {
  const [state, setState] = useState('empty'); // 'empty' | 'growing' | 'grown'
  const swayDir = (index % 2 === 0 ? 1 : -1);
  const swaySpeed = 2.8 + (index % 5) * 0.35;
  const swayDelay = (index * 0.12) % 2;

  useEffect(() => {
    if (!plant) { setState('empty'); return; }
    // Staggered entrance — each plant waits before growing
    const delay = Math.min(index * 55, 1200);
    const t = setTimeout(() => {
      setState('growing');
      setTimeout(() => setState('grown'), 680);
    }, delay);
    return () => clearTimeout(t);
  }, [plant, index]);

  if (!plant) {
    return (
      <div className="aspect-square rounded-xl flex items-center justify-center"
        style={{ background: 'rgba(255,255,255,0.04)' }}>
        <div className="w-1.5 h-1.5 rounded-full opacity-20" style={{ background: plant?.color || '#6ee7b7' }} />
      </div>
    );
  }

  return (
    <div className="relative aspect-square rounded-xl flex items-center justify-center overflow-visible"
      style={{ background: `linear-gradient(135deg, ${plant.color}18, ${plant.color}08)` }}>

      {/* New plant sparkle ring */}
      {isNew && state !== 'empty' && (
        <div className="absolute inset-0 rounded-xl sparkle-ring pointer-events-none"
          style={{ boxShadow: `0 0 0 2px ${plant.color}, 0 0 12px ${plant.glow}` }} />
      )}

      {/* The plant */}
      <span
        className={`text-lg select-none ${
          state === 'growing' ? 'plant-spring' : state === 'grown' ? 'plant-swaying' : 'opacity-0'
        }`}
        style={{
          '--sd': swayDir,
          '--si': index,
          animationDuration: state === 'grown' ? `${swaySpeed}s` : undefined,
          animationDelay: state === 'grown' ? `${swayDelay}s` : undefined,
          filter: state === 'grown' ? `drop-shadow(0 2px 4px ${plant.glow})` : undefined,
          transformOrigin: 'bottom center',
          display: 'block',
        }}
        role="img"
        aria-label={plant.emoji}
      >
        {plant.emoji}
      </span>
    </div>
  );
});

PlantCell.displayName = 'PlantCell';

// Milestone badge component
const MilestoneBadge = ({ milestone, isDarkMode }) => (
  <div
    className="milestone-pop flex items-center gap-2 px-3 py-1.5 rounded-full"
    style={{ background: `${milestone.color}22`, border: `1px solid ${milestone.color}55` }}
  >
    <span className="text-base">{milestone.icon}</span>
    <span className="text-xs font-bold" style={{ color: milestone.color }}>
      {milestone.name}
    </span>
  </div>
);

/**
 * JannahGardenCard — redesigned with proper spring animations
 */
const JannahGardenCard = ({ recitedCount: propCount }) => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL, language } = useTheme();

  const [recitedCount, setRecitedCount] = useState(() => {
    if (propCount !== undefined) return propCount;
    try { return JSON.parse(localStorage.getItem(RECITED_KEY) || '[]').length; }
    catch { return 0; }
  });
  const [prevCount, setPrevCount] = useState(recitedCount);
  const [newPlantIndex, setNewPlantIndex] = useState(-1);
  const [showMilestonePop, setShowMilestonePop] = useState(null);
  const prevMilestoneRef = useRef(getMilestone(recitedCount));

  // Sync from prop
  useEffect(() => {
    if (propCount === undefined) return;
    if (propCount > recitedCount) {
      setPrevCount(recitedCount);
      setNewPlantIndex(recitedCount); // index of new plant
      setRecitedCount(propCount);

      // Check milestone
      const newMilestone = getMilestone(propCount);
      const prevMilestone = prevMilestoneRef.current;
      if (newMilestone && newMilestone !== prevMilestone) {
        setTimeout(() => setShowMilestonePop(newMilestone), 700);
        setTimeout(() => setShowMilestonePop(null), 3500);
        prevMilestoneRef.current = newMilestone;
      }

      setTimeout(() => setNewPlantIndex(-1), 2000);
    } else {
      setRecitedCount(propCount);
    }
  }, [propCount]);

  const milestone = getMilestone(recitedCount);
  const nextMilestone = getNextMilestone(recitedCount);
  const progressPct = nextMilestone && milestone
    ? Math.round(((recitedCount - milestone.count) / (nextMilestone.count - milestone.count)) * 100)
    : nextMilestone ? Math.round((recitedCount / nextMilestone.count) * 100) : 100;

  const displayCount = Math.min(recitedCount, 30);
  const GRID_SIZE = 30;

  // Build plants array
  const plants = Array.from({ length: GRID_SIZE }, (_, i) =>
    i < displayCount ? getPlantAt(i) : null
  );

  const cardBg = isDarkMode
    ? 'from-[#012518] via-[#02311e] to-[#012518]'
    : 'from-[#ecfdf5] via-[#d1fae5] to-[#a7f3d0]';

  if (recitedCount === 0) {
    return (
      <div
        data-testid="jannah-garden"
        className={`rounded-3xl p-6 text-center ${isDarkMode ? 'glass-card' : 'bg-white shadow-sm border border-gray-100'}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="flex justify-center gap-3 mb-3 opacity-40">
          <StarOrnament size={24} color={isDarkMode ? '#6ee7b7' : '#059669'} />
          <span className="text-3xl">🌱</span>
          <StarOrnament size={24} color={isDarkMode ? '#6ee7b7' : '#059669'} />
        </div>
        <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {t('jannah.empty', 'Your Jannah Garden awaits')}
        </p>
        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
          {t('jannah.emptyHint', 'Recite a saved verse to plant your first seed')}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="jannah-garden"
      className={`rounded-3xl overflow-hidden relative bg-gradient-to-b ${cardBg} garden-glow`}
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ boxShadow: isDarkMode ? '0 0 30px rgba(16,185,129,0.15)' : '0 8px 32px rgba(5,150,105,0.15)' }}
    >
      {/* Sky decorations */}
      <div className="absolute top-2 right-5 opacity-25 pointer-events-none">
        <CrescentStarIcon size={18} color={isDarkMode ? '#fbbf24' : '#047857'} />
      </div>
      <div className="absolute top-3 left-8 opacity-15 pointer-events-none">
        <StarOrnament size={12} color={isDarkMode ? '#fbbf24' : '#047857'} />
      </div>

      {/* Milestone pop-in */}
      {showMilestonePop && (
        <div className="absolute top-14 left-0 right-0 flex justify-center z-20 pointer-events-none">
          <MilestoneBadge milestone={showMilestonePop} isDarkMode={isDarkMode} />
        </div>
      )}

      <div className="relative z-10 p-5">
        {/* Header */}
        <div className={`flex items-start justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={isRTL ? 'text-right' : ''}>
            <h3 className="font-bold text-base"
              style={{ color: isDarkMode ? '#6ee7b7' : '#065f46', fontFamily: language === 'ar' || language === 'ur' ? "'Noto Naskh Arabic', serif" : undefined }}>
              {t('jannah.gardenName', 'Jannah Garden')}
            </h3>
            {milestone && (
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-xs">{milestone.icon}</span>
                <span className="text-[11px] font-medium" style={{ color: milestone.color }}>
                  {language === 'ar' ? milestone.nameAr : milestone.name}
                </span>
              </div>
            )}
          </div>
          <div className={`text-right ${isRTL ? 'text-left' : ''}`}>
            <p className="text-3xl font-black tabular-nums" style={{ color: isDarkMode ? '#a7f3d0' : '#064e3b' }}>
              {recitedCount}
            </p>
            <p className="text-[10px] uppercase tracking-wide" style={{ color: isDarkMode ? '#6ee7b7' : '#047857' }}>
              {t('jannah.plants', 'plants')}
            </p>
          </div>
        </div>

        {/* Plant grid — the garden */}
        <div
          className="rounded-2xl p-3 mb-4 grid grid-cols-10 gap-1"
          style={{ background: isDarkMode ? 'rgba(1,37,24,0.5)' : 'rgba(6,78,59,0.06)' }}
        >
          {plants.map((plant, i) => (
            <PlantCell
              key={i}
              plant={plant}
              isNew={i === newPlantIndex}
              index={i}
              total={displayCount}
            />
          ))}
        </div>

        {/* Quranic quote */}
        <div className="rounded-xl px-3 py-2.5 mb-4"
          style={{ background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(6,78,59,0.06)' }}>
          <p className="text-center text-sm font-semibold"
            style={{ fontFamily: "'Amiri', 'Noto Naskh Arabic', serif", direction: 'rtl', lineHeight: '1.9',
              color: isDarkMode ? '#a7f3d0' : '#065f46' }}>
            وَبَشِّرِ الَّذِينَ آمَنُوا وَعَمِلُوا الصَّالِحَاتِ أَنَّ لَهُمْ جَنَّاتٍ
          </p>
          <p className="text-center text-[10px] mt-1"
            style={{ color: isDarkMode ? 'rgba(167,243,208,0.6)' : 'rgba(6,95,70,0.7)' }}>
            "Give glad tidings to those who believe... that they shall have gardens" — 2:25
          </p>
        </div>

        {/* Progress to next milestone */}
        {nextMilestone && (
          <div>
            <div className={`flex justify-between text-[10px] mb-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span style={{ color: isDarkMode ? '#6ee7b7' : '#047857' }}>
                {recitedCount} / {nextMilestone.count}
              </span>
              <span style={{ color: isDarkMode ? '#6ee7b7' : '#047857' }}>
                {nextMilestone.icon} {language === 'ar' ? nextMilestone.nameAr : nextMilestone.name}
              </span>
            </div>
            <div className="h-2 rounded-full overflow-hidden"
              style={{ background: isDarkMode ? 'rgba(16,185,129,0.15)' : 'rgba(5,150,105,0.12)' }}>
              <div className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${progressPct}%`,
                  background: `linear-gradient(to right, ${milestone?.color || '#6ee7b7'}, ${nextMilestone.color})`,
                  boxShadow: `0 0 8px ${nextMilestone.color}55`,
                }}
              />
            </div>
          </div>
        )}
        {!nextMilestone && (
          <div className="text-center text-sm font-bold milestone-pop"
            style={{ color: isDarkMode ? '#fbbf24' : '#92400e' }}>
            ✨ {t('jannah.complete', 'Garden of Jannah — Complete!')}
          </div>
        )}
      </div>
    </div>
  );
};

export default JannahGardenCard;
